/**
 * Typed wrapper around the `complete-child-signup` edge function
 * (Wave 3.4). Called from the parent-side child setup form after the
 * parent has signed in via the Supabase magic link from Wave 3.3.
 *
 * Server-side it: verifies caller is PARENT, creates the child's
 * auth.users + public.users + parent_children link rows, and triggers
 * the child's email-confirmation message.
 *
 * Mirrors `commitParentalConsent` in shape: env + session passed in,
 * `fetchImpl` injectable for tests.
 */

export interface CompleteChildSignupArgs {
  supabaseUrl: string;
  accessToken: string;
  childName: string;
  childEmail: string;
  childDob: string;        // ISO YYYY-MM-DD
  childPassword: string;
  fetchImpl?: typeof fetch;
}

export type CompleteChildSignupResult =
  | {
      ok: true;
      childUserId: string;
      childEmailSent: boolean;
      nextStep?: string;
    }
  | {
      ok: false;
      error: string;
      detail?: string;
      status?: number;
    };

export async function completeChildSignup(
  args: CompleteChildSignupArgs,
): Promise<CompleteChildSignupResult> {
  const fetchFn = args.fetchImpl ?? fetch;

  if (!args.supabaseUrl) {
    return { ok: false, error: 'Supabase env not configured.' };
  }
  if (!args.accessToken) {
    return { ok: false, error: 'You need to be signed in to add a child.' };
  }

  let resp: Response;
  try {
    resp = await fetchFn(`${args.supabaseUrl}/functions/v1/complete-child-signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${args.accessToken}`,
      },
      body: JSON.stringify({
        child_name: args.childName,
        child_email: args.childEmail,
        child_dob: args.childDob,
        child_password: args.childPassword,
      }),
    });
  } catch (e) {
    return {
      ok: false,
      error: 'Network error contacting child signup. Please try again.',
      detail: (e as Error)?.message,
    };
  }

  let body: unknown;
  try { body = await resp.json(); } catch { body = null; }
  const b = (body ?? {}) as Record<string, unknown>;

  if (!resp.ok || b.ok !== true) {
    return {
      ok: false,
      error: typeof b.error === 'string' ? b.error : `Could not create your child's account (HTTP ${resp.status}).`,
      detail: typeof b.detail === 'string' ? b.detail : undefined,
      status: resp.status,
    };
  }

  return {
    ok: true,
    childUserId: String(b.child_user_id ?? ''),
    childEmailSent: b.child_email_sent === true,
    nextStep: typeof b.next_step === 'string' ? b.next_step : undefined,
  };
}
